type CredentialResponse = {
  credential: string;
  select_by?: string;
};

type IdConfiguration = {
  client_id: string;
  callback: (response: CredentialResponse) => void;
  auto_select?: boolean;
  use_fedcm_for_prompt?: boolean;
};

type GsiButtonConfiguration = {
  type: "standard";
  theme: "outline";
  size: "large";
  text: "signin_with";
  shape: "rectangular";
  logo_alignment: "left";
  width: number;
};

type GoogleIdentityApi = {
  accounts: {
    id: {
      initialize: (configuration: IdConfiguration) => void;
      renderButton: (parent: HTMLElement, options: GsiButtonConfiguration) => void;
      disableAutoSelect: () => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentityApi;
  }
}

const scriptId = "google-identity-services";
let loader: Promise<GoogleIdentityApi> | null = null;

function configuredClientId(): string {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim();
  if (!clientId) {
    throw new Error("Google Client ID is not configured");
  }
  return clientId;
}

function loadGoogleIdentity(): Promise<GoogleIdentityApi> {
  if (window.google?.accounts.id) return Promise.resolve(window.google);
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.google?.accounts.id) resolve(window.google);
      else reject(new Error("Google Identity Services failed to load"));
    };
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener("error", () => reject(new Error("Google Identity Services failed to load")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = () => reject(new Error("Google Identity Services failed to load"));
    document.head.append(script);
  });
  return loader;
}

export async function renderGoogleSignInButton(
  parent: HTMLElement,
  onCredential: (credential: string) => void,
): Promise<void> {
  const google = await loadGoogleIdentity();
  parent.replaceChildren();
  google.accounts.id.initialize({
    client_id: configuredClientId(),
    callback: ({ credential }) => onCredential(credential),
    auto_select: false,
    use_fedcm_for_prompt: true,
  });
  const availableWidth = Math.floor(parent.getBoundingClientRect().width);
  google.accounts.id.renderButton(parent, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "signin_with",
    shape: "rectangular",
    logo_alignment: "left",
    width: Math.min(360, Math.max(240, availableWidth || 330)),
  });
}

export function disableGoogleAutoSelect(): void {
  window.google?.accounts.id.disableAutoSelect();
}
